import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { formatCurrency } from 'ddc-intl';
import logger from 'ws-scripts/modules/logger';
import { useFlags, useLabels, usePrefs } from 'wsm-common-data';
import useEventBus from '../hooks/useEventBus';
import usePaymentData from '../hooks/usePaymentData';
import useTrackEvent from '../hooks/useTrackEvent';
import PaymentSelectOption from './PaymentSelectOption';
import InlineDotsLoader from './InlineDotsLoader';
import DisclaimerText from './DisclaimerText';

import { setPayment } from '../features/steps/stepsSlice';
import { getLsWalletId } from '../helpers';
import './PaymentSelector.scss';

const PaymentUnavailableMessage = () => (
	<div className="payment-unavailable">
		<div className="payment-unavailable-content">
			<p className="payment-unavailable-title">
				Payments for this vehicle are unavailable.
			</p>
			<p className="payment-unavailable-subtitle">
				Please call for more information.
			</p>
		</div>
	</div>
);

const PaymentSelector = () => {
	const dispatch = useDispatch();
	const [selectedId, setSelectedId] = useState(0);
	const [isLoading, hasError, paymentData] = usePaymentData();
	const [eventBus, isReady] = useEventBus();
	const sendTrackEvent = useTrackEvent();
	const flags = useFlags();
	const enablePreload =
		flags['ws-track-my-offer-preload-payment-data'] || false;
	const labels = useLabels();
	const prefs = usePrefs();
	const { showTaxesAndFeesDisclaimer } = prefs;

	const showFinance = prefs.showFinance ?? 'true';
	const showLease = prefs.showLease ?? 'true';
	const showCash = prefs.showCash ?? 'true';

	const prefByType = useMemo(
		() => ({ finance: showFinance, lease: showLease, cash: showCash }),
		[showFinance, showLease, showCash]
	);

	const allPaymentTypesDisabled =
		showFinance === 'false' && showLease === 'false' && showCash === 'false';

	const filteredPaymentData = useMemo(
		() =>
			(paymentData || []).filter(
				(d) => prefByType[d.paymentLabel?.toLowerCase()] !== 'false'
			),
		[paymentData, prefByType]
	);

	const effectiveSelectedId = useMemo(() => {
		if (!filteredPaymentData.length) return 0;
		const originalSelected = paymentData?.[selectedId];
		const filteredIndex = filteredPaymentData.findIndex(
			(d) => d.key === originalSelected?.key
		);
		return filteredIndex !== -1 ? filteredIndex : 0;
	}, [filteredPaymentData, paymentData, selectedId]);

	const selectedPayment =
		filteredPaymentData.length &&
		filteredPaymentData[effectiveSelectedId]?.payment;
	const selectedDealType =
		filteredPaymentData.length &&
		filteredPaymentData[effectiveSelectedId]?.paymentLabel?.toLowerCase();

	// Extract taxes and fees flags from payment data
	const showTaxesAndFeesDisclaimerText = useMemo(
		() => paymentData?.some((data) => data.hasTaxesAndFees) || false,
		[paymentData]
	);

	useEffect(() => {
		if (isReady) {
			const handleUdscalcPencilingDataUpdateRequest = (data) => {
				if (data && paymentData) {
					const paymentItemId = paymentData.findIndex(
						(item) =>
							item.paymentLabel.toLowerCase() ===
							data.dealType?.toLowerCase()
					);

					if (paymentItemId !== selectedId) {
						setSelectedId(paymentItemId);
					}
				}
			};

			eventBus.on(
				'uds:calcPencilingDataUpdate:updated',
				handleUdscalcPencilingDataUpdateRequest
			);

			return () => {
				eventBus.off(
					'uds:calcPencilingDataUpdate:updated',
					handleUdscalcPencilingDataUpdateRequest
				);
			};
		}

		// default clean up
		return () => {};
	}, [isReady, eventBus, paymentData, selectedId]);

	// Set selectedId based on the index of the object that is the dealType (isSelected)
	useEffect(() => {
		if (paymentData?.length) {
			const selectedPaymentIndex = paymentData.findIndex(
				(data) => data.isSelected
			);

			setSelectedId(
				selectedPaymentIndex !== -1 ? selectedPaymentIndex : 0
			);
		}
	}, [paymentData]);

	useEffect(() => {
		const pubsub = window?.DDC?.pubsub;
		const hasInteractedWithWallet = !!getLsWalletId();
		let paymentToPublish = null;

		if (selectedPayment && selectedDealType !== 'cash') {
			paymentToPublish = formatCurrency(selectedPayment);
		}

		if (pubsub) {
			pubsub.publish('ws-vehicle-toolbar/show-vehicle-payment', {
				payment: paymentToPublish
			});
		}
		// TODO: handle payment error
		dispatch(
			setPayment({
				complete:
					hasInteractedWithWallet &&
					!!selectedPayment &&
					!!selectedDealType,
				paymentType: selectedDealType,
				total: selectedPayment,
				enabled: true
			})
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPayment, selectedDealType]);

	const getPlaceholders = () => {
		if (enablePreload) {
			const visibleTabs = [
				{ type: 'finance', label: 'Finance', pref: showFinance },
				{ type: 'lease',   label: 'Lease',   pref: showLease   },
				{ type: 'cash',    label: 'Cash',    pref: showCash    }
			].filter(({ pref }) => pref !== 'false');

			if (visibleTabs.length === 0) {
				return null;
			}

			const total = visibleTabs.length;

			const positionClass = (i) => {
				if (total === 1) return 'border rounded selected';
				if (i === 0)
					return 'border-left rounded-left left-option selected border rounded';
				if (i === total - 1)
					return 'border-right rounded-right right-option border-top border-bottom transparent';
				return 'middle-option border-top border-bottom transparent';
			};

			return (
				<div
					className="w-100 d-flex"
					style={{
						justifyContent: 'space-evenly',
						position: 'relative'
					}}
					data-preload-container="payment-options"
					data-testid="payment-options"
				>
					{visibleTabs.map(({ type, label }, index) => (
						<div
							key={type}
							className={`position-relative payment-option-container py-3 d-flex border justify-content-center ${positionClass(index)}`}
							style={{ flex: '1 1 0px' }}
							data-payment-type={type}
						>
							<label
								htmlFor={`payment_option_${index}_input`}
								className="my-0 font-weight-normal w-100"
							>
								<div className="d-flex flex-row justify-content-center mt-1">
									<input
										type="radio"
										className="mt-0 mr-2"
										id={`payment_option_${index}_input`}
										value={label}
										name="paymentSelection"
										defaultChecked={index === 0}
										disabled
									/>
									<span className="ddc-font-size-small">{label}</span>
								</div>
								<div className="text-center">
									<span className="font-weight-bold ddc-font-size-base">
										<InlineDotsLoader />
									</span>
									{type !== 'cash' && (
										<span className="text-muted ddc-font-size-xsmall">
											{' '}/ mo
										</span>
									)}
								</div>
							</label>
						</div>
					))}
					{/* one inset-border div per gap between adjacent tabs */}
					{total > 1 &&
						Array.from({ length: total - 1 }, (_, i) => (
							<div
								key={`inset-border-${i + 1}`}
								className="inset-border"
								style={{ display: 'none' }}
								data-inset-border={i + 1}
							/>
						))}
				</div>
			);
		}

		return (
			<div
				className="skeleton-shimmer border rounded"
				style={{ height: '70px', minHeight: '70px' }}
				data-testid="payment-skeleton-shimmer"
			>
				<div className="d-flex flex-row">
					<div className="ddc-font-size-small"> </div>
				</div>
			</div>
		);
	};

	const containerStyle = {
		justifyContent: 'space-evenly'
	};

	if (allPaymentTypesDisabled) {
		return <PaymentUnavailableMessage />;
	}

	if (hasError && !paymentData) {
		if (enablePreload) {
			logger.warn('WS-TRACK-MY-OFFER: payment data unavailable, showing error state');
			return <PaymentUnavailableMessage />;
		}
		logger.error('WS-TRACK-MY-OFFER: payment data unavailable, hiding widget');
		return null;
	}

	const onPaymentSelect = (id) => {
		const selectedItem = filteredPaymentData[id];
		const dealType = selectedItem?.paymentLabel?.toLowerCase();

		const originalIndex = paymentData?.findIndex(
			(d) => d.key === selectedItem?.key
		);
		setSelectedId(originalIndex !== -1 ? originalIndex : id);

		eventBus?.emit('uds:setDealType:request', {
			dealType
		});

		sendTrackEvent({
			element: 'Build Offer Toggle',
			elementCTA: selectedItem?.paymentLabel,
			result: 'Change deal type'
		});
	};

	const getPaymentSelectOptions = () => {
		const hasValidPayment = filteredPaymentData.some((data) => data.payment > 0);

		if (!hasValidPayment) {
			return null;
		}

		return filteredPaymentData.map((data, index) => {
			const {
				payment,
				paymentTerm,
				paymentLabel,
				hasSpecial,
				key,
				hasTaxesAndFees,
				hasError: hasPaymentError,
				firstItem,
				lastItem
			} = data;

			return (
				<PaymentSelectOption
					id={index}
					paymentAmount={payment}
					term={paymentTerm}
					paymentTypeLabel={paymentLabel}
					isSelected={effectiveSelectedId === index}
					onSelect={onPaymentSelect}
					firstItem={firstItem}
					lastItem={lastItem}
					hasSpecial={hasSpecial}
					isLoading={isLoading}
					key={key}
					hasTaxesAndFees={
						hasTaxesAndFees && showTaxesAndFeesDisclaimer === 'true'
					}
					hasError={hasPaymentError}
				/>
			);
		});
	};

	if (enablePreload && (isLoading || !paymentData)) {
		return <div className="pt-4 rounded">{getPlaceholders()}</div>;
	}

	if (!isLoading && paymentData && filteredPaymentData.length === 0) {
		return <PaymentUnavailableMessage />;
	}

	const paymentOptions =
		isLoading || !paymentData
			? getPlaceholders()
			: getPaymentSelectOptions();

	logger.info('WS-TRACK-MY-OFFER: PaymentSelector final render', {
		isLoading,
		hasPaymentData: !!paymentData,
		paymentDataLength: paymentData?.length || 0,
		hasPaymentOptions: !!paymentOptions,
		enablePreload
	});

	return (
		!!paymentOptions && (
			<>
				<div className="d-flex pt-4 rounded" style={containerStyle}>
					{paymentOptions}
				</div>
				{showTaxesAndFeesDisclaimerText &&
					showTaxesAndFeesDisclaimer === 'true' && (
						<DisclaimerText
							text={labels.get('DISCLAIMER_TAXES_AND_FEES')}
							key="taxes-fees-disclaimer"
						/>
					)}
			</>
		)
	);
};

export default PaymentSelector;
